import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { USER_API } from "config";
import { ApiErrorCode } from "config/errorCodes";
import { useAxios } from "hooks/useAxios";
import { useStepUpProtectedAction } from "hooks/auth";
import { workspaceKeys } from "queries";
import { handleToast, ServerError } from "utils";
import { getToastMessageForError } from "utils/errorHandling";

export const MCP_CONNECTOR_CLIENTS = ["claude", "chatgpt", "cursor"] as const;
export type McpConnectorClient = (typeof MCP_CONNECTOR_CLIENTS)[number];

export type McpConnectorStatus =
  | { connected: false }
  | {
      connected: true;
      api_key_id: string;
      name: string;
      created_at: string;
      expires_at: string | null;
      first_used_at: string | null;
      last_used_at: string | null;
      scopes: string[];
    };

export type McpConnectorCreated = {
  client: McpConnectorClient;
  api_key_id: string;
  connector_url: string;
  install_url: string | null;
  admin_install_url: string | null;
  command: string | null;
  expires_at: string | null;
  scopes: string[];
  name: string;
  created_at: string;
  first_used_at: string | null;
  last_used_at: string | null;
};

const isReauthenticationCancelled = (error: unknown): boolean =>
  error instanceof Error && error.message === "Re-authentication cancelled";

export const isMcpConnectorLicenseError = (error: unknown): boolean => {
  const serverError =
    error instanceof ServerError
      ? error
      : isAxiosError(error)
        ? ServerError.fromAxiosError(error)
        : null;
  if (serverError?.code === ApiErrorCode.CLAUDE_CONNECTOR_LICENSE_REQUIRED) return true;
  if (!isAxiosError(error)) return false;
  return (
    (error.response?.data as { code?: string } | undefined)?.code ===
    ApiErrorCode.CLAUDE_CONNECTOR_LICENSE_REQUIRED
  );
};

const toastForConnectorError = (error: unknown, fallback: string): void => {
  if (isReauthenticationCancelled(error) || isMcpConnectorLicenseError(error)) return;
  const serverError = isAxiosError(error)
    ? ServerError.fromAxiosError(error)
    : error instanceof ServerError
      ? error
      : null;
  handleToast(serverError ? getToastMessageForError(serverError) : fallback, "error");
};

export const useMcpConnector = (workspaceId: string | undefined, client: McpConnectorClient) => {
  const axios = useAxios({ allResponse: true });
  const queryClient = useQueryClient();
  const { runProtectedAction } = useStepUpProtectedAction();
  const queryKey = workspaceKeys.mcpConnector(workspaceId ?? "", client);
  const enabled = Boolean(workspaceId);

  const statusQuery = useQuery<McpConnectorStatus>({
    queryKey,
    queryFn: async () => {
      const response = await axios.get<{ data: McpConnectorStatus }>(
        USER_API.getWorkspaceMcpConnector(workspaceId!, client)
      );
      return response.data.data;
    },
    enabled,
    refetchOnWindowFocus: true,
    refetchInterval: (data) => (data?.connected && !data.first_used_at ? 5000 : false),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({
      queryKey: workspaceKeys.apiKeys(workspaceId ?? "", "mine"),
    });
    void queryClient.invalidateQueries({
      queryKey: workspaceKeys.apiKeys(workspaceId ?? "", "all"),
    });
  };

  const createMutation = useMutation({
    mutationFn: async () =>
      runProtectedAction(async () => {
        const response = await axios.post<{ data: McpConnectorCreated }>(
          USER_API.createWorkspaceMcpConnector(workspaceId!, client)
        );
        return response.data.data;
      }),
    onSuccess: invalidate,
    onError: (error: unknown) => toastForConnectorError(error, `Could not connect ${client}.`),
  });

  const disconnectMutation = useMutation({
    mutationFn: async () =>
      runProtectedAction(async () => {
        await axios.delete(USER_API.deleteWorkspaceMcpConnector(workspaceId!, client));
      }),
    onSuccess: () => {
      invalidate();
      handleToast("Connection disconnected.", "success");
    },
    onError: (error: unknown) => toastForConnectorError(error, `Could not disconnect ${client}.`),
  });

  return {
    statusQuery,
    createMutation,
    disconnectMutation,
    isCreating: createMutation.isPending,
    isDisconnecting: disconnectMutation.isPending,
  };
};
